import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';
import { copyToClipboard, round } from '../../../../../../helpers';
import { useAppDispatch } from '../../../../../hooks';
import { pushMessage } from '../../../../../../features/global-messages/global-messages-slice';
import {
    CREDIT_NOTE_AMOUNT,
    IOrderSale,
    REQUIRE_CREDIT_NOTE,
    SUBTOTAL,
    TAX,
    TOTAL_WITH_TAX,
    TRANSFER_RECEIPTS_TOTAL,
} from './order-sale-form';
import { useReceiptAppliesTax } from './helpers/use-receipt-applies-tax';

interface IOrderSaleUtilidadesProps {
    subtotal: number;
    tax: number;
    totalWithTax: number;
    // Real money received: transfer receipts minus transfer adjustments
    // (transfer_receipts_total_no_adjustments).
    realPaymentsTotal: number;
}

/**
 * Nota de crédito calculator — the "Herramientas" tab of the sale form, also
 * shown in the optional-details dialog.
 *
 * Clients pay an agreed share of the invoice (98 %, 95 % …) as an on-time
 * discount, and goods they return are never paid either. The nota de crédito
 * closes the whole gap between the invoice (the CFDI figure) and the money that
 * actually arrived, so it covers returns + discount in one number:
 *
 *   con IVA = total_with_tax − transfer_receipts_total_no_adjustments
 *   sin IVA = con IVA ÷ (1 + tax / subtotal)   ← the sale's own effective rate
 *
 * Transfer adjustments are deliberately not counted as payments: recording the
 * discount as one must not make the credit note shrink. It is always shown,
 * even mid-payment — the user knows when a sale will not be paid further.
 */
export function OrderSaleUtilidades({
    subtotal,
    tax,
    totalWithTax,
    realPaymentsTotal,
}: IOrderSaleUtilidadesProps) {
    const dispatch = useAppDispatch();
    // Untyped on purpose: shared by the full sale form and the details form,
    // which both register `credit_note_amount` / `require_credit_note`.
    const { setValue } = useFormContext();
    const appliesTax = useReceiptAppliesTax();

    const conIva = totalWithTax - realPaymentsTotal;
    const taxMultiplier = subtotal !== 0 ? 1 + tax / subtotal : 1;
    const sinIva = conIva / taxMultiplier;

    const handleCopy = async (value: string) => {
        const ok = await copyToClipboard(value);
        dispatch(
            pushMessage({
                message: ok ? 'Copiado al portapapeles' : 'No se pudo copiar',
                options: { variant: ok ? 'success' : 'error' },
            }),
        );
    };

    const handleUseAsCreditNote = () => {
        // The amount input is disabled until the requirement is checked.
        setValue(REQUIRE_CREDIT_NOTE, true, { shouldDirty: true });
        setValue(CREDIT_NOTE_AMOUNT, round(conIva), {
            shouldDirty: true,
            shouldValidate: true,
        });
        dispatch(
            pushMessage({
                message: 'Monto aplicado a la nota de crédito',
                options: { variant: 'success' },
            }),
        );
    };

    // 4 decimals, no thousands separators — what's shown is what's copied, so it
    // pastes cleanly into Excel.
    const items = [
        {
            label: 'Nota de credito (sin IVA)',
            value: sinIva.toFixed(4),
        },
        {
            label: 'Nota de credito (con IVA)',
            value: conIva.toFixed(4),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant={'body2'} color={'text.secondary'}>
                Total facturado menos pagos reales (sin ajustes): incluye
                devoluciones y descuento por pronto pago.
            </Typography>
            {conIva < 0 ? (
                <Alert severity={'warning'}>
                    Los pagos superan el total facturado. Revisa las
                    transferencias de esta venta.
                </Alert>
            ) : null}
            {items.map((item) => (
                <Box
                    key={item.label}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant={'caption'} color={'text.secondary'}>
                            {item.label}
                        </Typography>
                        <Typography
                            variant={'body2'}
                            sx={{ wordBreak: 'break-all' }}
                        >
                            {item.value}
                        </Typography>
                    </Box>
                    <Button
                        variant={'outlined'}
                        size={'small'}
                        startIcon={<ContentCopy />}
                        onClick={() => void handleCopy(item.value)}
                    >
                        Copiar
                    </Button>
                </Box>
            ))}
            <Box>
                <Button
                    variant={'contained'}
                    size={'small'}
                    disabled={!appliesTax}
                    onClick={handleUseAsCreditNote}
                >
                    Usar monto (con IVA) en la nota de crédito
                </Button>
                {!appliesTax ? (
                    <Typography
                        variant={'caption'}
                        color={'text.secondary'}
                        sx={{ display: 'block', mt: 0.5 }}
                    >
                        El tipo de recibo no aplica nota de crédito.
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}

// Sale-form flavor: reads the live form values, so the amount follows price
// edits before saving. Real payments can't be edited here, so that side is the
// saved figure.
export function OrderSaleUtilidadesLive() {
    const [subtotal, tax, totalWithTax, realPaymentsTotal] = useWatch({
        name: [SUBTOTAL, TAX, TOTAL_WITH_TAX, TRANSFER_RECEIPTS_TOTAL],
    }) as [
        IOrderSale['subtotal'],
        IOrderSale['tax'],
        IOrderSale['total_with_tax'],
        IOrderSale['transfer_receipts_total_no_adjustments'],
    ];

    return (
        <OrderSaleUtilidades
            subtotal={Number(subtotal) || 0}
            tax={Number(tax) || 0}
            totalWithTax={Number(totalWithTax) || 0}
            realPaymentsTotal={Number(realPaymentsTotal) || 0}
        />
    );
}
